import {
  Button,
  Container,
  Grid,
  Group,
  Select,
  useMantineTheme,
} from '@mantine/core';
import { DateRangePicker } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { UseFormReturnType } from '@mantine/form/lib/use-form';
import { showNotification } from '@mantine/notifications';
import { ReactNode, useEffect, useState } from 'react';
import { CalendarMinus, X } from 'tabler-icons-react';
import { industryMidData } from '../../data/list-mid.array';
import { RetrieveAllTransactionsMode } from '../../enums/retrieve-transaction.enums';
import { loadTransactionStatuses } from '../../helpers/filters.helpers';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import { IPaginationMeta } from '../../type/admin.types';
import { ErrorResponse } from '../../type/error-response.types';
import {
  AdminTransactionSelectData,
  EntityFilterLoading,
  SelectData,
  TransactionFilterForm,
} from '../../type/filter.types';
import {
  BasicTransactionResponseDetails,
  BasicTransactionResponseSummary,
  ExportTransactionsRequest,
  RetrieveAllTransactionsRequest,
  TransactionStats,
} from '../../type/retrieve-transaction.types';
import { BasicBranch, BasicPos } from '../../type/select.types';

const ResponsiveCol = ({ children }: { children: ReactNode }) => {
  return (
    <Grid.Col span={12} sm={6} md={3}>
      {children}
    </Grid.Col>
  );
};

const MerchantTransactionFilter = ({
  setData,
  form,
  setPaginationData,
  page,
  setPage,
  filterSelectData,
  isLoadBranches,
  setIsLoadBranches,
  setTransactionSummaryRequest,
  transactionSummaryRequest,
  data,
  isChecked,
  setTransactionDetails,
  setDetailsPaginationData,
  setTransactionDetailsRequest,
  setShowTransactionSummary,
  setTransactionStats,
}: {
  setData: React.Dispatch<
    React.SetStateAction<BasicTransactionResponseSummary[]>
  >;
  setPaginationData: React.Dispatch<
    React.SetStateAction<IPaginationMeta | undefined>
  >;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  page: number;
  form: UseFormReturnType<TransactionFilterForm>;
  filterSelectData: UseFormReturnType<AdminTransactionSelectData>;
  setIsLoadBranches: React.Dispatch<React.SetStateAction<boolean>>;
  isLoadBranches: boolean;
  setTransactionSummaryRequest: React.Dispatch<
    React.SetStateAction<RetrieveAllTransactionsRequest | undefined>
  >;
  transactionSummaryRequest: RetrieveAllTransactionsRequest | undefined;
  data: BasicTransactionResponseSummary[];
  isChecked: number[];
  setTransactionDetails: React.Dispatch<
    React.SetStateAction<BasicTransactionResponseDetails[] | undefined>
  >;
  setDetailsPaginationData: React.Dispatch<
    React.SetStateAction<IPaginationMeta | undefined>
  >;
  setTransactionDetailsRequest: React.Dispatch<
    React.SetStateAction<RetrieveAllTransactionsRequest | undefined>
  >;
  setShowTransactionSummary: React.Dispatch<React.SetStateAction<boolean>>;
  setTransactionStats: React.Dispatch<
    React.SetStateAction<TransactionStats | undefined>
  >;
}) => {
  const [dontUseEffect, setDontUseEffect] = useState(false);
  const selectLoadingData = useForm<EntityFilterLoading>({
    initialValues: {
      isResellersLoading: false,
      isMerchantsLoading: false,
      isBranchesLoading: false,
      isPosLoading: false,
      isStatusesLoading: false,
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetailedViewButton, setIsLoadingDetailedViewButton] =
    useState(false);
  const [isLoadingExportButton, setIsLoadingExportButton] = useState(false);

  const axiosPrivate = useAxiosPrivate();

  const theme = useMantineTheme();

  const disableFields =
    form.values.dateRange[0] === null || form.values.dateRange[1] === null;
  const noData = 'No data';
  const loadingData = 'Loading...';

  const findValueInData = (data: SelectData[], value: string): boolean => {
    for (let i = 0; i < data?.length; i++) {
      if (data[i].value === value) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    let ignore = false;

    const loadMerchantBranches = async () => {
      selectLoadingData.setFieldValue('isBranchesLoading', true);

      try {
        const response = await axiosPrivate.get('/web/select/branches');

        const merchantBranches = response.data?.items?.map(
          (merchant: BasicBranch) => {
            return {
              label: merchant.branchName,
              value: merchant.branchId.toString(),
            };
          }
        );

        if (!ignore) {
          const found = findValueInData(
            merchantBranches,
            form.values.merchantBranch
          );

          if (!found) {
            form.setFieldValue('merchantBranch', '');
          }
          filterSelectData.setFieldValue(
            'merchantBranchesData',
            merchantBranches
          );
        }
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-branches-data',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        selectLoadingData.setFieldValue('isBranchesLoading', false);
      }
    };

    if (isLoadBranches) {
      loadMerchantBranches();
      loadTransactionStatuses(
        axiosPrivate,
        filterSelectData,
        selectLoadingData
      );
    } else {
      form.setFieldValue('merchantBranch', '');
      setIsLoadBranches(false);
    }

    return () => {
      ignore = true;
    };
  }, []);

  useDidMountEffect(() => {
    let ignore = false;

    const loadPos = async () => {
      selectLoadingData.setFieldValue('isPosLoading', true);

      try {
        const response = await axiosPrivate.get('/web/select/pos', {
          params: {
            branchId: form.values.merchantBranch,
          },
        });

        const pos = response.data?.items?.map((merchant: BasicPos) => {
          return {
            label: merchant.posName,
            value: merchant.posId.toString(),
          };
        });

        if (!ignore) {
          const found = findValueInData(pos, form.values.pos);

          if (!found) {
            form.setFieldValue('pos', '');
          }
          filterSelectData.setFieldValue('posData', pos);
        }
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-pos-data',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        selectLoadingData.setFieldValue('isPosLoading', false);
      }
    };

    if (form.values.merchantBranch) {
      loadPos();
    } else {
      form.setFieldValue('pos', '');
      filterSelectData.setFieldValue('posData', []);
    }

    return () => {
      ignore = true;
    };
  }, [form.values.merchantBranch]);

  const createPaginationRequest = async (page: number) => {
    try {
      const request: RetrieveAllTransactionsRequest = {
        mode: RetrieveAllTransactionsMode.SUMMARY,
        page: page || 1,
        filters: {
          ...transactionSummaryRequest!.filters,
        },
        detailedFilters: [],
      };

      const filters = request.filters;
      const detailedFilters = request.detailedFilters;
      if (!filters.limit) {
        delete filters.limit;
      }
      if (!filters.resellerId) {
        delete filters.resellerId;
      }
      if (!filters.merchantId) {
        delete filters.merchantId;
      }
      if (!filters.branchId) {
        delete filters.branchId;
      }
      if (!filters.posId) {
        delete filters.posId;
      }
      detailedFilters.forEach((detailedFilter) => {
        if (!detailedFilter.transactionStatusId) {
          delete detailedFilter.transactionStatusId;
        }
        if (!detailedFilter.industryMid) {
          delete detailedFilter.industryMid;
        }
      });

      const response = await axiosPrivate.post('/web/transactions', request);

      setTransactionSummaryRequest(request);
      setPaginationData(response.data?.metadata);
      setData(response.data?.items);
      setTransactionStats(response.data?.stats);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-retrieve-next-pagination-data',
        message: error.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    }
  };

  const createRequest = async (page?: number) => {
    let startDate = form.values.dateRange[0]!;
    let offset = startDate.getTimezoneOffset();
    startDate = new Date(startDate.getTime() - offset * 60 * 1000);
    const formatStartDate = startDate.toISOString().split('T')[0] + ' 00:00:00';

    let endDate = form.values.dateRange[1]!;
    offset = endDate.getTimezoneOffset();
    endDate = new Date(endDate.getTime() - offset * 60 * 1000);
    const formatEndDate = endDate.toISOString().split('T')[0] + ' 23:59:59';

    try {
      const request: RetrieveAllTransactionsRequest = {
        mode: RetrieveAllTransactionsMode.SUMMARY,
        page: page || 1,
        filters: {
          limit: 20,
          startDate: formatStartDate,
          endDate: formatEndDate,
          branchId: parseInt(form.values.merchantBranch),
          posId: parseInt(form.values.pos),
        },
        detailedFilters: [
          {
            transactionStatusId: parseInt(form.values.transactionStatus),
            industryMid: form.values.industryMid,
          },
        ],
      };

      const filters = request.filters;
      const detailedFilters = request.detailedFilters;
      if (!filters.limit) {
        delete filters.limit;
      }
      if (!filters.resellerId) {
        delete filters.resellerId;
      }
      if (!filters.merchantId) {
        delete filters.merchantId;
      }
      if (!filters.branchId) {
        delete filters.branchId;
      }
      if (!filters.posId) {
        delete filters.posId;
      }
      detailedFilters.forEach((detailedFilter) => {
        if (!detailedFilter.transactionStatusId) {
          delete detailedFilter.transactionStatusId;
        }
        if (!detailedFilter.industryMid) {
          delete detailedFilter.industryMid;
        }
      });

      const response = await axiosPrivate.post('/web/transactions', request);

      setTransactionSummaryRequest(request);
      setPaginationData(response.data?.metadata);
      setData(response.data?.items);
      setTransactionStats(response.data?.stats);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-apply-filters',
        message: error.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    }
  };

  const handleDetailedView = async () => {
    setIsLoadingDetailedViewButton(true);
    let transactionDetails: BasicTransactionResponseDetails[] = [];
    let response: any;

    let startDate = form.values.dateRange[0]!;
    let offset = startDate.getTimezoneOffset();
    startDate = new Date(startDate.getTime() - offset * 60 * 1000);
    const formatStartDate = startDate.toISOString().split('T')[0] + ' 00:00:00';

    let endDate = form.values.dateRange[1]!;
    offset = endDate.getTimezoneOffset();
    endDate = new Date(endDate.getTime() - offset * 60 * 1000);
    const formatEndDate = endDate.toISOString().split('T')[0] + ' 23:59:59';

    try {
      let request: RetrieveAllTransactionsRequest = {
        mode: RetrieveAllTransactionsMode.DETAILS,
        page: 1,
        filters: {
          ...transactionSummaryRequest!.filters,
        },
        detailedFilters: [],
      };

      for (let i: number = 0; i < isChecked.length; i++) {
        let selected: BasicTransactionResponseSummary = data[isChecked[i]];
        request.detailedFilters.push({
          transactionStatusId: selected.transactionStatusId,
          industryMid: selected.industryMid,
          cardTypeId: selected.cardTypeId,
          currencyId: selected.currencyId,
          transactionTypeId: selected.transactionTypeId,
        });
      }

      const filters = request.filters;
      const detailedFilters = request.detailedFilters;
      if (!filters.limit) {
        delete filters.limit;
      }
      if (!filters.resellerId) {
        delete filters.resellerId;
      }
      if (!filters.merchantId) {
        delete filters.merchantId;
      }
      if (!filters.branchId) {
        delete filters.branchId;
      }
      if (!filters.posId) {
        delete filters.posId;
      }
      detailedFilters.forEach((detailedFilter) => {
        if (!detailedFilter.transactionStatusId) {
          delete detailedFilter.transactionStatusId;
        }
        if (!detailedFilter.industryMid) {
          delete detailedFilter.industryMid;
        }
      });

      response = await axiosPrivate.post('/web/transactions', request);

      transactionDetails = transactionDetails.concat(response.data.items);

      setTransactionDetails(transactionDetails);
      setDetailsPaginationData(response.data.metadata);
      setTransactionDetailsRequest(request);
      setShowTransactionSummary(false);
      setIsLoadBranches(false);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;
      showNotification({
        id: 'failed-to-retrieve-transaction-details',
        message: error.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoadingDetailedViewButton(false);
    }
  };

  const handleExport = async () => {
    setIsLoadingExportButton(true);
    try {
      const request: ExportTransactionsRequest = {
        filters: {
          partnerId: transactionSummaryRequest?.filters.partnerId!,
          ...transactionSummaryRequest!.filters,
        },
        detailedFilters: [],
      };

      for (let i: number = 0; i < isChecked.length; i++) {
        let selected: BasicTransactionResponseSummary = data[isChecked[i]];
        request.detailedFilters.push({
          transactionStatusId: selected.transactionStatusId,
          industryMid: selected.industryMid,
          cardTypeId: selected.cardTypeId,
          currencyId: selected.currencyId,
          transactionTypeId: selected.transactionTypeId,
        });
      }

      const filters = request.filters;
      const detailedFilters = request.detailedFilters;
      if (!filters.resellerId) {
        delete filters.resellerId;
      }
      if (!filters.merchantId) {
        delete filters.merchantId;
      }
      if (!filters.branchId) {
        delete filters.branchId;
      }
      if (!filters.posId) {
        delete filters.posId;
      }
      detailedFilters.forEach((detailedFilter) => {
        if (!detailedFilter.transactionStatusId) {
          delete detailedFilter.transactionStatusId;
        }
        if (!detailedFilter.industryMid) {
          delete detailedFilter.industryMid;
        }
      });

      const response = await axiosPrivate.post(
        '/web/transactions/export',
        request,
        {
          responseType: 'blob',
        }
      );

      const startNoTime = transactionSummaryRequest!.filters.startDate
        .toString()
        .split(' ');
      const endNoTime = transactionSummaryRequest!.filters.endDate
        .toString()
        .split(' ');

      const formattedStartDate = startNoTime[0].split('-').join('');
      const formattedEndDate = endNoTime[0].split('-').join('');
      const fileName = `${formattedStartDate}-${formattedEndDate}`;

      const blob = response.data;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      link.click();
    } catch (err: any) {
      let responseObj = await err.response.data.text();
      const error: ErrorResponse = JSON.parse(responseObj);
      showNotification({
        id: 'failed-to-retrieve-partners-data',
        message:
          error.error ||
          err.response?.request.statusText ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoadingExportButton(false);
    }
  };

  const handleSubmit = async () => {
    if (page > 1) {
      setDontUseEffect(true);
    }
    createRequest();
    setPage(1);
  };

  useDidMountEffect(() => {
    if (dontUseEffect) {
      setDontUseEffect(false);
      return;
    }
    createPaginationRequest(page);
  }, [page]);

  return (
    <form
      className='transactions-filter'
      onSubmit={form.onSubmit(handleSubmit)}
    >
      <Container pb='md' ml={0}>
        <Grid>
          <Grid.Col span={12} sm={6} md={6}>
            <DateRangePicker
              label='Date Range'
              dayStyle={(date) =>
                date.getDate() === new Date().getDate() &&
                date.getMonth() === new Date().getMonth() &&
                (form.values.dateRange.length !== 2 ||
                  (form.values.dateRange[0] === null &&
                    form.values.dateRange[1] === null))
                  ? {
                      backgroundColor: theme.colors.blue[6],
                      color: theme.white,
                    }
                  : {}
              }
              onChange={(date) => {
                form.setFieldValue('dateRange', date);
              }}
              icon={
                <CalendarMinus size={20} strokeWidth={2} color={'#a4a0a0'} />
              }
              value={form.values.dateRange}
              allowSingleDateInRange
              clearable
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <ResponsiveCol>
            <Select
              label='Select Merchant Branch'
              data={
                selectLoadingData.values.isBranchesLoading
                  ? [loadingData]
                  : filterSelectData.values.merchantBranchesData?.length === 0
                    ? [noData]
                    : filterSelectData.values.merchantBranchesData
              }
              value={
                selectLoadingData.values.isBranchesLoading
                  ? loadingData
                  : filterSelectData.values.merchantBranchesData?.length === 0
                    ? noData
                    : form.values.merchantBranch
              }
              disabled={
                selectLoadingData.values.isBranchesLoading ||
                filterSelectData.values.merchantBranchesData?.length === 0
              }
              onChange={(value: string) =>
                form.setFieldValue('merchantBranch', value)
              }
              clearable={
                !(
                  selectLoadingData.values.isBranchesLoading ||
                  filterSelectData.values.merchantBranchesData?.length === 0
                )
              }
              searchable
            />
          </ResponsiveCol>
        </Grid>

        <Grid>
          <ResponsiveCol>
            <Select
              label='Select POS'
              data={
                selectLoadingData.values.isPosLoading
                  ? [loadingData]
                  : filterSelectData.values.posData?.length === 0 &&
                      form.values.merchantBranch
                    ? [noData]
                    : filterSelectData.values.posData
              }
              value={
                selectLoadingData.values.isPosLoading
                  ? loadingData
                  : filterSelectData.values.posData?.length === 0 &&
                      form.values.merchantBranch
                    ? noData
                    : form.values.pos
              }
              disabled={
                selectLoadingData.values.isPosLoading ||
                filterSelectData.values.posData?.length === 0 ||
                form.values.merchantBranch?.length === 0
              }
              onChange={(value: string) => form.setFieldValue('pos', value)}
              clearable={
                !(
                  selectLoadingData.values.isPosLoading ||
                  filterSelectData.values.posData?.length === 0 ||
                  form.values.merchantBranch?.length === 0
                )
              }
              searchable
            />
          </ResponsiveCol>
          <ResponsiveCol>
            <Select
              label='Select Industry MID'
              data={industryMidData}
              onChange={(value: string) =>
                form.setFieldValue('industryMid', value)
              }
              value={form.values.industryMid}
              clearable
              searchable
            />
          </ResponsiveCol>
          <ResponsiveCol>
            <Select
              label='Transaction Status'
              data={
                selectLoadingData.values.isStatusesLoading
                  ? [loadingData]
                  : filterSelectData.values.transactionStatusData?.length === 0
                    ? [noData]
                    : filterSelectData.values.transactionStatusData
              }
              value={
                selectLoadingData.values.isStatusesLoading
                  ? loadingData
                  : filterSelectData.values.transactionStatusData?.length === 0
                    ? noData
                    : form.values.transactionStatus
              }
              disabled={
                selectLoadingData.values.isStatusesLoading ||
                filterSelectData.values.transactionStatusData?.length === 0
              }
              onChange={(value: string) =>
                form.setFieldValue('transactionStatus', value)
              }
              clearable={
                !(
                  selectLoadingData.values.isStatusesLoading ||
                  filterSelectData.values.transactionStatusData?.length === 0
                )
              }
              searchable
            />
          </ResponsiveCol>
        </Grid>

        <Group position='apart'>
          <Button
            mt='lg'
            type='submit'
            disabled={disableFields}
            onSubmit={handleSubmit}
            loading={isLoading}
          >
            Apply
          </Button>

          <Group position='right'>
            <Button
              mt='lg'
              type='submit'
              disabled={isChecked.length <= 0}
              loading={isLoadingDetailedViewButton}
              onClick={handleDetailedView}
            >
              Detailed View
            </Button>
            <Button
              mt='lg'
              type='submit'
              disabled={isChecked.length <= 0}
              loading={isLoadingExportButton}
              onClick={handleExport}
            >
              Export
            </Button>
          </Group>
        </Group>
      </Container>
    </form>
  );
};

export default MerchantTransactionFilter;
